import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { loanListOptionParams } from '../utils/lending-filters.util';
import { loanListSortParams } from '../utils/loan-list-sort.util';
import { environment } from 'src/environments/environment';
import { WebStorageService } from './web-storage.service';

@Injectable({
    providedIn: 'root'
})
export class BorrowLendService {

    constructor(
        private http: HttpClient,
        private webStorageService: WebStorageService
    ) { }

    public counterOffer(counterOfferData: any) {
        return this.http.post(`${environment.API_BASE_URL_V3}/loan-request-bid`, counterOfferData);
    }

    public getActiveNegotiationById(id: string) {
        return this.http.get(`${environment.API_BASE_URL_V3}/bids/user-loan-requests?id=${id}`);
    }

    public getLendingActivity(page: number, limit: number, sort: string) {
        return this.http.get(`${environment.API_BASE_URL_V3}/list-loan-details?page=${page}&limit=${limit}&loan_details=${sort}`);
    }

    /**
     * Gets lending activity for a user.
     * @param page - Page number to retrieve.
     * @param limit - Number of records to retrieve per page.
     * @param type - Type of lending activity to retrieve. Defaults to 'all' if not provided.
     * @returns Observable of lending activity response.
     */
    public getAllItems(endpoint: string,page: number, limit: number, type: string, filterParams?: any) {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
      
        if (type && type !== 'all') {
          params.append('type', type);
        }

        if (filterParams?.minApr != null) params.set('MINAPR', filterParams.minApr.toString());
        if (filterParams?.maxApr != null) params.set('MAXAPR', filterParams.maxApr.toString());
        if (filterParams?.minLtv != null) params.set('MINLTV', filterParams.minLtv.toString());
        if (filterParams?.maxLtv != null) params.set('MAXLTV', filterParams.maxLtv.toString());
        this.appendLoanListParams(params, filterParams);
        // Asset or collection name search (All loans), sent only when typed.
        if (filterParams?.search) params.set('search', filterParams.search);

        return this.http.get(`${environment.API_BASE_URL_V3}/${endpoint}?${params.toString()}`);
    }
      

    public getLoanRequestsById(userId: string, page: number, limit: number, filterParams?: any) {
        const params = new URLSearchParams();
        params.set('id', userId);
        params.set('page', page.toString());
        params.set('limit', limit.toString());

        if (filterParams?.type) params.set('type', filterParams.type);
        if (filterParams?.minApr != null) params.set('MINAPR', filterParams.minApr.toString());
        if (filterParams?.maxApr != null) params.set('MAXAPR', filterParams.maxApr.toString());
        if (filterParams?.minLtv != null) params.set('MINLTV', filterParams.minLtv.toString());
        if (filterParams?.maxLtv != null) params.set('MAXLTV', filterParams.maxLtv.toString());
        this.appendLoanListParams(params, filterParams);
        // Asset or collection name search, sent only when typed.
        if (filterParams?.search) params.set('search', filterParams.search);

        return this.http.get(`${environment.API_BASE_URL_V3}/active-loan-requests?${params.toString()}`);
    }


    public getLoanRequests(page: number, limit: number, filterParams?: any) {
        const params = new URLSearchParams();

        params.set('page', page.toString());
        params.set('limit', limit.toString());

        if (filterParams?.type) params.set('type', filterParams.type);
        if (filterParams?.minApr != null) params.set('MINAPR', filterParams.minApr.toString());
        if (filterParams?.maxApr != null) params.set('MAXAPR', filterParams.maxApr.toString());
        if (filterParams?.minLtv != null) params.set('MINLTV', filterParams.minLtv.toString());
        if (filterParams?.maxLtv != null) params.set('MAXLTV', filterParams.maxLtv.toString());
        this.appendLoanListParams(params, filterParams);
        // Asset or collection name search, sent only when typed.
        if (filterParams?.search) params.set('search', filterParams.search);

        return this.http.get(`${environment.API_BASE_URL_V3}/active-loan-requests?${params.toString()}`);
        // return this.http.get(`${environment.API_BASE_URL_V3}/active-loan-requests?page=${page}&limit=${limit}&type=${type}&MINAPR=${minApr}&MAXAPR=${maxApr}&MINLTV=${minLtv}&MAXLTV=${maxLtv}`);
        // return this.http.get(`${environment.API_BASE_URL_V3}/active-loan-requests?page=${page}&limit=${limit}`);
    }

    public getUsersLiveBorrowing(page: number, limit: number, sort: string) {
        return this.http.get(`${environment.API_BASE_URL_V3}/borrowers-accepted-unaccepted-loans?page=${page}&limit=${limit}&action=${sort}`);
    }

    public getUsersLiveBorrowingNew(page: number, limit: number, type: string, filterParams?: any) {
        const params = new URLSearchParams({
            page: String(page),
            limit: String(limit),
          });
        
          if (type && type !== 'all') {
            params.append('type', type);
          }
          if (filterParams?.minApr != null) params.set('MINAPR', filterParams.minApr.toString());
          if (filterParams?.maxApr != null) params.set('MAXAPR', filterParams.maxApr.toString());
          if (filterParams?.minLtv != null) params.set('MINLTV', filterParams.minLtv.toString());
          if (filterParams?.maxLtv != null) params.set('MAXLTV', filterParams.maxLtv.toString());
          this.appendLoanListParams(params, filterParams);
          // Collateral asset or collection name; searched by the API, sent only when typed.
          if (filterParams?.search) params.set('search', filterParams.search);
        return this.http.get(`${environment.API_BASE_URL_V3}/live-borrowing-list?${params.toString()}`);
    }

    /**
     * The checkbox groups (loan type, interest type, repayment, single / bundle) and the sort
     * (`filterParams.sort`) for the Borrowing / Lending lists, sent only when set so an
     * unfiltered, unsorted request is unchanged. The API applies them (LOAN_TYPES,
     * INTEREST_TYPES, REPAYMENT_TYPES, ASSET_TYPES, sort_by / sort_order).
     */
    private appendLoanListParams(params: URLSearchParams, filterParams?: any): void {
        const listParams = { ...loanListOptionParams(filterParams), ...loanListSortParams(filterParams?.sort) };
        for (const [key, value] of Object.entries(listParams)) {
            params.set(key, value);
        }
    }

    public getLenderLiveBorrowing(userId: string) {
        return this.http.get(`${environment.API_BASE_URL_V3}/lender-live-loans?id=${userId}`);
    }

    public getRepayedAndForceClosedLoans(page: number, limit: number, sort: string) {
        return this.http.get(`${environment.API_BASE_URL_V3}/repaid-closed-loans?page=${page}&limit=${limit}&action=${sort}`);
    }

    public getNftsNotRequestedForLoan(borrowerId: string, collectionAddress: string, tokenId: number) {
        return this.http.get(`${environment.API_BASE_URL_V3}/check-nft-available?address=${collectionAddress}&token_id=${tokenId}&id=${borrowerId}`);
    }

    public repayLoan(params: any) {
        return this.http.post(`${environment.API_BASE_URL_V3}/repay-force-close-loan-request`, params);
    }

    async getSignature(params: any) {
        try {
            let user = JSON.parse(this.webStorageService.getLocalStorage('user') || '{}');
            params.accountId = user.fire_block_address;
            const result = await this.http.post(`${environment.API_BASE_URL_V3}/fireblock-signature`, params).toPromise();
            return result;
        } catch (error) {
            console.error('Error fetching data:', error);
            throw error;
        }
    }

    async getLoanOfferSignature(params: any) {
        try {
            let user = JSON.parse(this.webStorageService.getLocalStorage('user') || '{}');
            params.accountId = user.fire_block_address;
            const result = await this.http.post(`${environment.API_BASE_URL_V3}/open-loan-request-bid-signature`, params).toPromise();
            return result;
        } catch (error) {
            console.error('Error fetching data:', error);
            throw error;
        }
    }

    public getUserLendingStatus() {
        return this.http.get(`${environment.API_BASE_URL_V3}/is-lending-activity`);
    }

    public getUserBorrowStatus() {
        return this.http.get(`${environment.API_BASE_URL_V3}/user-live-loans`);
    }

    async getLoanStatus(id: string) {
        let loanStatus: any = await this.http.get(`${environment.API_BASE_URL_V3}/is-loan-completed?id=${id}`).toPromise();
        return loanStatus.data.is_loan_completed
    }
    /**
    * Handles counter offer
    * @param offer_id 
    * @param request 
    * @returns  
    */
    public handleCounterOffer(offer_id: string, request: any) {
        return this.http.patch(`${environment.API_BASE_URL_V3}/loan-request-bid/update-cancel?id=${offer_id}`, request)
    }
    /**
     * Handlgets all live loan
     * @param [limit] 
     * @param [page] 
     * @returns  
     */
    public handlgetAllLiveLoan(limit: number = 10, page: number = 1) {
        return this.http.get(`${environment.API_BASE_URL_V3}/all-active-loan-requests?limit=${limit}&page=${page}`)
    }
    
    public notifyAmountInsufficientWhileAcceptRefinanceRequest(refinanceLoanRequestId: string, data: any) {
        return this.http.post(`${environment.API_BASE_URL_V3}/notify-refinance-amount-insufficient?id=${refinanceLoanRequestId}`, data);
    }
}
